import { useCallback, useEffect, useMemo, useState, memo } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useUpdateUser, useDeleteConversations } from "@/hooks/useAuth";
import { DeleteAccount } from "./DeleteAccount";
import { DeleteConversations } from "./DeleteConversations";
import { cn } from "@/lib/utils";
import { AccountSheetProps } from '@/types/componentsTypes';


export function AccountSheet({ open, onOpenChange }: AccountSheetProps) {
  const { t } = useLanguage();
  const { user, isAuthed } = useCurrentUser();
  const updateUser = useUpdateUser();

  const [saveSuccess, setSaveSuccess] = useState(false);

  if (!user || !isAuthed) return null;

  const initial = useMemo(
    () => ({
      firstName: user.firstName?.trim() ?? "",
      lastName: user.lastName?.trim() ?? "",
      avatarUrl: user.avatarUrl ?? undefined,
      email: user.emailAddress ?? "",
      id: user.id,
    }),
    [user]
  );

  const [form, setForm] = useState({
    firstName: initial.firstName,
    lastName: initial.lastName,
    avatarUrl: initial.avatarUrl ?? "",
  });


  // Clear prior success state when user edits again
  useEffect(() => {
    setSaveSuccess(false);
  }, [form.firstName, form.lastName, form.avatarUrl]);

  const saving = updateUser.isPending;

  const trimmedFirst = form.firstName.trim();
  const trimmedLast = form.lastName.trim();

  const hasDiffs =
    trimmedFirst !== initial.firstName ||
    trimmedLast !== initial.lastName;

  const canSave =
    hasDiffs &&
    trimmedFirst.length > 0 &&
    trimmedLast.length > 0 &&
    !saving;

  const onSave = useCallback(async () => {
    if (!canSave) return;
    await updateUser.mutateAsync({
      userId: initial.id,
      data: {
        firstName: trimmedFirst,
        lastName: trimmedLast,
      },
    });

    setSaveSuccess(true);
    window.setTimeout(() => setSaveSuccess(false), 2500);
  }, [
    canSave,
    updateUser,
    initial.id,
    trimmedFirst,
    trimmedLast,
  ]);

  const closeSheet = useCallback(() => onOpenChange(false), [onOpenChange]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="sm:max-w-[560px] w-full overflow-y-auto"
        key={initial.id}
      >
        <SheetHeader>
          <SheetTitle>{t("accountTitle")}</SheetTitle>
          <SheetDescription>{t("accountSubtitle")}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 flex flex-col gap-4 pb-24">
          <div className="space-y-2">
            <Label htmlFor="email">{t("email")}</Label>
            <Input id="email" value={initial.email} disabled />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="firstName">{t("firstName")}</Label>
              <Input
                id="firstName"
                value={form.firstName}
                onChange={(e) =>
                  setForm((s) => ({ ...s, firstName: e.target.value }))
                }
                disabled={saving}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">{t("lastName")}</Label>
              <Input
                id="lastName"
                value={form.lastName}
                onChange={(e) =>
                  setForm((s) => ({ ...s, lastName: e.target.value }))
                }
                disabled={saving}
              />
            </div>
          </div>
          <DangerZone className="mt-6" disabled={saving} onClose={closeSheet} />
        </div>

        <div className="sticky bottom-0 left-0 right-0 bg-background border-t mt-4 pt-3 pb-3 flex justify-end gap-2">
          {saveSuccess && (
            <div className="mr-auto self-center text-sm text-green-600 font-medium">
              {t("saved")}
            </div>
          )}
          <Button onClick={onSave} disabled={!canSave}>
            {saving ? t("saving") : t("saveChanges")}
          </Button>
          <Button variant="secondary" onClick={closeSheet} disabled={saving}>
            {t("close")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

const DangerZone = memo(function DangerZone({
  className,
  disabled,
  onClose,
}: {
  className?: string;
  disabled?: boolean;
  onClose: () => void;
}) {
  const deleteConversations = useDeleteConversations();

  return (
    <div className={cn("space-y-3", className)}>
      <Label>Delete data</Label>
      <DeleteConversations
        className="mt-0"
        disabled={disabled}
        onDeleteAll={async (password) => {
          await deleteConversations.mutateAsync(password);
        }}
      />
      <DeleteAccount className="mt-0" disabled={disabled} onSuccess={onClose} />
    </div>
  );
});
